/**
 * Estado do montador de operações.
 *
 * O grafo vive no cliente (arrastar tem que ser instantâneo) e o cálculo fiscal
 * vive no servidor. O `debounce` existe para que arrastar um nó não dispare uma
 * requisição por quadro.
 */

import * as React from "react";

import { gravarSessao, lerSessao } from "@/lib/sessao";
import type { Aresta, Contexto, No, PedidoCalculo, Produto, TipoNo } from "@/types";

export interface EstadoOperacao {
  nome: string;
  nodes: No[];
  edges: Aresta[];
  produto: Produto;
  anidroDiferido: boolean;
  precoCompra: string;
  precoVenda: string;
  volume: string;
  /** Ordem de emissão fixada pelo usuário no roteiro fiscal (chaves das NFs). */
  ordemNfs?: string[];
  /** Deslocamento que o usuário deu a cada balão de seta, pela chave da seta. */
  baloes?: Record<string, { dx: number; dy: number }>;
}

const UF_PADRAO: Partial<Record<TipoNo, string>> = {
  PRODUTOR: "MT",
  EXTERIOR: "EX",
};

/** Espaço livre no canvas para um nó novo, sem sobrepor os existentes. */
function posicaoLivre(nodes: No[]): { x: number; y: number } {
  for (let y = 24; y <= 340; y += 105) {
    for (let x = 40; x <= 840; x += 180) {
      const ocupado = nodes.some(
        (n) =>
          typeof n.x === "number" &&
          typeof n.y === "number" &&
          Math.abs(n.x - x) < 170 &&
          Math.abs(n.y - y) < 95,
      );
      if (!ocupado) return { x, y };
    }
  }
  return {
    x: Math.min(840, 40 + (nodes.length % 5) * 170),
    y: Math.min(340, 24 + Math.floor(nodes.length / 5) * 105),
  };
}

/**
 * Garante que todos os nós possuam coordenadas válidas e organizadas em estágios,
 * evitando que apareçam empilhados no canvas.
 */
export function organizarNos(nodes: No[], edges: Aresta[]): No[] {
  if (!nodes.length) return [];

  const produtor = nodes.find((n) => n.tipo === "PRODUTOR");
  const interm = nodes.find((n) => n.tipo === "ECE" || n.tipo === "DISTFS");
  const cliente = nodes.find((n) => n.tipo === "DISTRIBUIDORA" || n.tipo === "EXTERIOR");
  const temArmazem = nodes.some((n) => n.tipo === "ARMAZEM");

  const todosTemCoord = nodes.every(
    (n) => typeof n.x === "number" && typeof n.y === "number" && !isNaN(n.x) && !isNaN(n.y),
  );
  const saoMesmoPonto =
    todosTemCoord && new Set(nodes.map((n) => `${Math.round(n.x)},${Math.round(n.y)}`)).size <= 1;
  const saoLinhaPlana =
    todosTemCoord && nodes.every((n) => Math.abs(n.y - 220) < 5);

  // 1. Caso canônico: Triangulação clássica com 3 nós (Produtor + ECE/DistFS + Cliente sem armazém)
  if (
    nodes.length === 3 &&
    produtor &&
    interm &&
    cliente &&
    !temArmazem &&
    (!todosTemCoord || saoMesmoPonto || saoLinhaPlana)
  ) {
    return nodes.map((n) => {
      if (n.id === interm.id) return { ...n, x: 440, y: 40 };
      if (n.id === produtor.id) return { ...n, x: 140, y: 340 };
      if (n.id === cliente.id) return { ...n, x: 740, y: 340 };
      return n;
    });
  }

  // 2. Caso com Armazém (Produtor + ECE + Armazém + Exterior/Cliente)
  if (
    nodes.length === 4 &&
    produtor &&
    interm &&
    cliente &&
    temArmazem &&
    (!todosTemCoord || saoMesmoPonto || saoLinhaPlana)
  ) {
    const armazem = nodes.find((n) => n.tipo === "ARMAZEM")!;
    return nodes.map((n) => {
      if (n.id === produtor.id) return { ...n, x: 60, y: 300 };
      if (n.id === interm.id) return { ...n, x: 340, y: 40 };
      if (n.id === armazem.id) return { ...n, x: 340, y: 340 };
      if (n.id === cliente.id) return { ...n, x: 740, y: 190 };
      return n;
    });
  }

  // 3. Armazenagem com 3 nós (Produtor + ECE + Armazém)
  if (
    nodes.length === 3 &&
    produtor &&
    interm &&
    temArmazem &&
    (!todosTemCoord || saoMesmoPonto || saoLinhaPlana)
  ) {
    const armazem = nodes.find((n) => n.tipo === "ARMAZEM")!;
    return nodes.map((n) => {
      if (n.id === interm.id) return { ...n, x: 440, y: 40 };
      if (n.id === produtor.id) return { ...n, x: 140, y: 340 };
      if (n.id === armazem.id) return { ...n, x: 740, y: 340 };
      return n;
    });
  }

  // Se já possuem coordenadas distintas e válidas, preserva o layout existente:
  if (todosTemCoord && nodes.length > 1 && !saoMesmoPonto) {
    return nodes;
  } else if (todosTemCoord && nodes.length === 1 && (nodes[0].x > 0 || nodes[0].y > 0)) {
    return nodes;
  }

  const ordemTipo: Record<string, number> = {
    PRODUTOR: 0,
    ECE: 1,
    DISTFS: 1,
    ARMAZEM: 2,
    DISTRIBUIDORA: 3,
    EXTERIOR: 3,
  };

  const estagios = new Map<number, number>();
  for (const n of nodes) {
    estagios.set(n.id, ordemTipo[n.tipo] ?? 1);
  }

  // Propagação topológica a partir das arestas
  for (let iter = 0; iter < nodes.length; iter++) {
    let mudou = false;
    for (const e of edges) {
      const estDe = estagios.get(e.de);
      const estPara = estagios.get(e.para);
      if (estDe !== undefined && estPara !== undefined && estPara <= estDe) {
        estagios.set(e.para, estDe + 1);
        mudou = true;
      }
    }
    if (!mudou) break;
  }

  // Agrupa nós por coluna
  const porColuna = new Map<number, No[]>();
  for (const n of nodes) {
    const col = estagios.get(n.id) ?? 0;
    const lista = porColuna.get(col) ?? [];
    lista.push(n);
    porColuna.set(col, lista);
  }

  const colunas = Array.from(porColuna.keys()).sort((a, b) => a - b);
  const totalCols = colunas.length;

  let xs: number[];
  if (totalCols <= 1) {
    xs = [440];
  } else if (totalCols === 2) {
    xs = [160, 640];
  } else if (totalCols === 3) {
    xs = [40, 380, 720];
  } else if (totalCols === 4) {
    xs = [40, 290, 550, 800];
  } else {
    const passo = Math.floor(800 / (totalCols - 1));
    xs = colunas.map((_, i) => 40 + i * passo);
  }

  const mapaColX = new Map<number, number>();
  colunas.forEach((col, i) => mapaColX.set(col, xs[i]));

  const resultado: No[] = [];
  for (const col of colunas) {
    const grupo = porColuna.get(col) ?? [];
    const x = mapaColX.get(col) ?? 440;
    const k = grupo.length;

    let ys: number[];
    if (k === 1) {
      if (grupo[0].tipo === "ECE" || grupo[0].tipo === "DISTFS") {
        ys = [40];
      } else if (totalCols >= 3 && (grupo[0].tipo === "PRODUTOR" || grupo[0].tipo === "DISTRIBUIDORA")) {
        ys = [340];
      } else {
        ys = [220];
      }
    } else if (k === 2) {
      ys = [70, 310];
    } else if (k === 3) {
      ys = [30, 200, 370];
    } else {
      // 470 = ALTURA_CANVAS do FlowCanvas; 388 = último topo que ainda cabe.
      const passoY = Math.min(110, Math.floor(364 / (k - 1)));
      const baseY = Math.max(24, Math.floor((470 - (k * 82 + (k - 1) * 20)) / 2));
      ys = grupo.map((_, j) => baseY + j * passoY);
    }

    grupo.forEach((n, j) => {
      resultado.push({
        ...n,
        x,
        y: ys[j],
      });
    });
  }

  return resultado;
}

/*
 * Dimensões do nó e do canvas, espelhadas do FlowCanvas para o cálculo das
 * montagens do botão "Ordenar".
 */
const LARGURA_NO_ORD = 184;
const ALTURA_NO_ORD = 82;
const LARGURA_CANVAS_ORD = 1040;
const ALTURA_CANVAS_ORD = 470;

const MODO_ORDENACOES = ["poligono", "linha", "grade", "estagios"] as const;

const clampX = (x: number) => Math.max(0, Math.min(LARGURA_CANVAS_ORD - LARGURA_NO_ORD, Math.round(x)));
const clampY = (y: number) => Math.max(0, Math.min(ALTURA_CANVAS_ORD - ALTURA_NO_ORD, Math.round(y)));

/**
 * Ordem estável dos nós para as montagens: papéis primeiro (produtor →
 * intermediadores → clientes) e, dentro do mesmo papel, a ordem de inserção.
 * Sem isso o polígono embaralha quem é quem e as setas cruzam o desenho.
 */
function nosOrdenadosPorPapel(nodes: No[]): No[] {
  const ordemTipo: Record<string, number> = {
    PRODUTOR: 0,
    ECE: 1,
    DISTFS: 1,
    ARMAZEM: 2,
    DISTRIBUIDORA: 3,
    EXTERIOR: 3,
  };
  return [...nodes].sort(
    (a, b) => (ordemTipo[a.tipo] ?? 1) - (ordemTipo[b.tipo] ?? 1) || a.id - b.id,
  );
}

/**
 * Duas linhas simétricas, metade dos nós em cada: 4 nós desenham um quadrado,
 * 5 um retângulo 3+2, 6 o retângulo 3+3. Cada linha é dividida em fatias iguais
 * e o nó toma o centro da sua — simetria exata para qualquer quantidade.
 */
function posicoesDuasLinhas(k: number): Array<{ x: number; y: number }> {
  const topo = Math.ceil(k / 2);
  const yTopo = 0;
  // Recuo na linha de baixo: o balão de sombra/legenda da seta transborda a
  // caixa em ~26px com balão de duas linhas e, com o nó colado em y=388, empurra uma barra de rolagem.
  const yBaixo = ALTURA_CANVAS_ORD - ALTURA_NO_ORD - 48;
  return Array.from({ length: k }, (_, i) => {
    const naParteDeCima = i < topo;
    const m = naParteDeCima ? topo : k - topo;
    const idx = naParteDeCima ? i : i - topo;
    return {
      x: m === 1 ? (LARGURA_CANVAS_ORD - LARGURA_NO_ORD) / 2 : (idx * (LARGURA_CANVAS_ORD - LARGURA_NO_ORD)) / (m - 1),
      y: naParteDeCima ? yTopo : yBaixo,
    };
  });
}

/**
 * Montagem "cadeia em cima, armazéns embaixo": a linha superior é a cadeia de
 * negócio (produtor → ECE/DistFS → clientes) na ordem dos papéis, e a inferior
 * são os armazéns ordenados pela posição deles no fluxo (estágio topológico),
 * exatamente como um oleoduto desenhado de cabo a rabo. Devolve as posições na
 * mesma ordem de `nosOrdenadosPorPapel`, ou null quando não há armazéns (nesse
 * caso vale o polígono puro).
 */
function posicoesCadeiaEArmazens(nodes: No[], edges: Aresta[]): Array<{ x: number; y: number }> | null {
  const armazens = nodes.filter((n) => n.tipo === "ARMAZEM");
  const cadeia = nosOrdenadosPorPapel(nodes.filter((n) => n.tipo !== "ARMAZEM"));
  if (!armazens.length || cadeia.length < 2) return null;

  // Estágio topológico para ordenar os armazéns ao longo do fluxo
  const estagio = new Map<number, number>();
  for (const n of nodes) estagio.set(n.id, n.tipo === "PRODUTOR" ? 0 : 1);
  for (let iter = 0; iter < nodes.length; iter++) {
    let mudou = false;
    for (const e of edges) {
      const de = estagio.get(e.de) ?? 1;
      const para = estagio.get(e.para) ?? 1;
      if (para <= de) {
        estagio.set(e.para, de + 1);
        mudou = true;
      }
    }
    if (!mudou) break;
  }
  const armazensOrdenados = [...armazens].sort(
    (a, b) => (estagio.get(a.id) ?? 0) - (estagio.get(b.id) ?? 0) || a.id - b.id,
  );

  const yTopo = 0;
  // Recuo na linha de baixo: o balão de sombra da caixa transborda o nó e,
  // colado em y=388, empurra uma barra de rolagem vertical no canvas.
  const yBaixo = ALTURA_CANVAS_ORD - ALTURA_NO_ORD - 48;
  const fatia = (grupo: No[], y: number): Array<{ x: number; y: number }> =>
    grupo.map((_, idx) => ({
      // De borda a borda: o primeiro nó encosta em x=0 e o último em x=856,
      // sem margem — nada de barra de rolagem no canvas.
      x:
        grupo.length === 1
          ? (LARGURA_CANVAS_ORD - LARGURA_NO_ORD) / 2
          : (idx * (LARGURA_CANVAS_ORD - LARGURA_NO_ORD)) / (grupo.length - 1),
      y,
    }));

  const mapa = new Map<number, { x: number; y: number }>();
  fatia(cadeia, yTopo).forEach((p, i) => mapa.set(cadeia[i].id, p));
  fatia(armazensOrdenados, yBaixo).forEach((p, i) => mapa.set(armazensOrdenados[i].id, p));

  const ordenados = nosOrdenadosPorPapel(nodes);
  return ordenados.map((n) => mapa.get(n.id)!);
}

/**
 * Polígono: 3 nós formam um triângulo; a partir de 4, duas linhas simétricas
 * (4 = quadrado, 5 = retângulo 3+2, 6 = retângulo 3+3) — a elipse ia ficando
 * achatada e diagonal demais com nós de 184x82.
 */
function posicoesPoligono(k: number): Array<{ x: number; y: number }> {
  if (k === 1) return [{ x: 428, y: 194 }];
  const cx = LARGURA_CANVAS_ORD / 2;
  const cy = ALTURA_CANVAS_ORD / 2;
  if (k === 2) return [{ x: cx - 240, y: cy }, { x: cx + 56, y: cy }];
  if (k === 3) {
    return [
      { x: cx - LARGURA_NO_ORD / 2, y: 0 },
      { x: 0, y: ALTURA_CANVAS_ORD - ALTURA_NO_ORD - 48 },
      { x: LARGURA_CANVAS_ORD - LARGURA_NO_ORD, y: ALTURA_CANVAS_ORD - ALTURA_NO_ORD - 48 },
    ];
  }
  return posicoesDuasLinhas(k);
}

/** Linha horizontal única, centrada na altura do canvas. */
function posicoesLinha(k: number): Array<{ x: number; y: number }> {
  const y = (ALTURA_CANVAS_ORD - ALTURA_NO_ORD) / 2;
  if (k === 1) return [{ x: 428, y }];
  const passo = (LARGURA_CANVAS_ORD - LARGURA_NO_ORD) / (k - 1);
  return Array.from({ length: k }, (_, i) => ({ x: i * passo, y }));
}

/** Grade: colunas de leitura natural, no máximo 4 por linha. */
function posicoesGrade(k: number): Array<{ x: number; y: number }> {
  const colunas = Math.min(4, Math.ceil(Math.sqrt(k)));
  const linhas = Math.ceil(k / colunas);
  const passoX = (LARGURA_CANVAS_ORD - LARGURA_NO_ORD) / Math.max(1, colunas - 1 || 1);
  const passoY = linhas > 1 ? (ALTURA_CANVAS_ORD - ALTURA_NO_ORD - 48) / (linhas - 1) : 0;
  const y0 = linhas > 1 ? 0 : (ALTURA_CANVAS_ORD - ALTURA_NO_ORD) / 2;
  return Array.from({ length: k }, (_, i) => ({
    x: i % colunas === 0 ? 0 : i % colunas * passoX,
    y: y0 + Math.floor(i / colunas) * passoY,
  }));
}

/**
 * Aplica uma montagem ao fluxo. "estagios" reaproveita o `organizarNos` — para
 * isso os nós entram sem coordenadas, senão ele preserva o layout atual.
 */
function aplicarOrdenacao(nodes: No[], edges: Aresta[], modo: (typeof MODO_ORDENACOES)[number]): No[] {
  if (modo === "estagios") {
    const limpos = nodes.map(({ x: _x, y: _y, ...resto }) => resto as No);
    return organizarNos(limpos, edges);
  }
  if (modo === "poligono") {
    /*
      "Cadeia e armazéns" quando o fluxo pede: com armazéns presentes, a linha
      de cima é a cadeia de negócio e a de baixo os armazéns na ordem do fluxo.
      Sem armazéns (ou cadeia curta demais), vale o polígono puro.
    */
    const cadeiaArmazens = posicoesCadeiaEArmazens(nodes, edges);
    if (cadeiaArmazens) {
      const ordenados = nosOrdenadosPorPapel(nodes);
      const porId = new Map(ordenados.map((n, i) => [n.id, cadeiaArmazens[i]]));
      return nodes.map((n) => {
        const p = porId.get(n.id);
        return p ? { ...n, x: clampX(p.x), y: clampY(p.y) } : n;
      });
    }
  }

  const ordenados = nosOrdenadosPorPapel(nodes);
  const posicoes =
    modo === "poligono" ? posicoesPoligono(ordenados.length)
    : modo === "linha" ? posicoesLinha(ordenados.length)
    : posicoesGrade(ordenados.length);
  const porId = new Map(ordenados.map((n, i) => [n.id, posicoes[i]]));
  return nodes.map((n) => {
    const p = porId.get(n.id);
    return p ? { ...n, x: clampX(p.x), y: clampY(p.y) } : n;
  });
}

/** `chave` separa o estado de sub-abas que dividem o mesmo contexto (ex.: Produtora). */
export function useOperacao(inicial: EstadoOperacao, contexto: Contexto, chave: string = contexto) {
  const chaveSessao = `ole:fluxo:${chave}`;

  /*
    O estado salvo na sessão vence o inicial: sem isso, voltar de outro menu
    remontava a página e o fluxo-modelo reaparecia — inclusive depois de
    "Limpar". Lido uma vez só, na montagem.
  */
  const [salvo] = React.useState(() => {
    const lido = lerSessao<EstadoOperacao>(chaveSessao);
    return lido && Array.isArray(lido.nodes) && Array.isArray(lido.edges) ? lido : null;
  });
  const base = salvo ?? inicial;

  const [estado, setEstado] = React.useState<EstadoOperacao>(() => ({
    ...base,
    nodes: salvo ? salvo.nodes : organizarNos(inicial.nodes, inicial.edges),
  }));
  const [ligandoDe, setLigandoDe] = React.useState<number | null>(null);
  const [modoLigar, setModoLigar] = React.useState(false);

  React.useEffect(() => {
    gravarSessao(chaveSessao, estado);
  }, [chaveSessao, estado]);

  const proximoIdNo = React.useRef(
    Math.max(0, ...base.nodes.map((n) => n.id)) + 1,
  );
  const proximoIdAresta = React.useRef(
    Math.max(0, ...base.edges.map((e) => e.id)) + 1,
  );

  const alterar = React.useCallback(
    <C extends keyof EstadoOperacao>(campo: C, valor: EstadoOperacao[C]) =>
      setEstado((s) => ({ ...s, [campo]: valor })),
    [],
  );

  const substituirGrafo = React.useCallback((nodes: No[], edges: Aresta[]) => {
    proximoIdNo.current = Math.max(0, ...nodes.map((n) => n.id)) + 1;
    proximoIdAresta.current = Math.max(0, ...edges.map((e) => e.id)) + 1;
    const nosPosicionados = organizarNos(nodes, edges);
    // Grafo novo: a ordem das notas e os balões arrastados eram do desenho anterior.
    setEstado((s) => ({ ...s, nodes: nosPosicionados, edges, ordemNfs: [], baloes: {} }));
    setLigandoDe(null);
    setModoLigar(false);
  }, []);

  const adicionarNo = React.useCallback((tipo: TipoNo) => {
    setEstado((s) => {
      const { x, y } = posicaoLivre(s.nodes);
      const uf = UF_PADRAO[tipo] ?? (contexto === "produtora" ? "MT" : "SP");
      return {
        ...s,
        nodes: [...s.nodes, { id: proximoIdNo.current++, tipo, uf, x, y }],
      };
    });
  }, [contexto]);

  const removerNo = React.useCallback((id: number) => {
    setEstado((s) => ({
      ...s,
      nodes: s.nodes.filter((n) => n.id !== id),
      edges: s.edges.filter((e) => e.de !== id && e.para !== id),
    }));
  }, []);

  const atualizarNo = React.useCallback((id: number, campos: Partial<No>) => {
    setEstado((s) => ({
      ...s,
      nodes: s.nodes.map((n) => (n.id === id ? { ...n, ...campos } : n)),
    }));
  }, []);

  const removerAresta = React.useCallback((id: number) => {
    setEstado((s) => ({ ...s, edges: s.edges.filter((e) => e.id !== id) }));
  }, []);

  /** Clique no nó durante o modo "ligar": primeiro clique escolhe a origem. */
  const clicarNo = React.useCallback(
    (id: number) => {
      if (!modoLigar) return;
      if (ligandoDe === null) {
        setLigandoDe(id);
        return;
      }
      if (ligandoDe === id) {
        setLigandoDe(null);
        return;
      }
      setEstado((s) => {
        const existe = s.edges.some((e) => e.de === ligandoDe && e.para === id);
        if (existe) return s;
        return {
          ...s,
          edges: [...s.edges, { id: proximoIdAresta.current++, de: ligandoDe, para: id }],
        };
      });
      setLigandoDe(null);
      setModoLigar(false);
    },
    [modoLigar, ligandoDe],
  );

  const alternarModoLigar = React.useCallback(() => {
    setModoLigar((v) => !v);
    setLigandoDe(null);
  }, []);

  /*
    Botão "Ordenar": cada clique aplica a montagem seguinte do ciclo (polígono,
    linha, grade, estágios) e volta à primeira — o usuário clica até achar a
    configuração que preferir para a operação em mãos. O avanço do modo acontece
    FORA do updater: em StrictMode o updater roda duas vezes e o modo pulava
    uma montagem a cada clique.
  */
  const modoOrdenacao = React.useRef(-1);
  const ordenarFluxo = React.useCallback(() => {
    modoOrdenacao.current = (modoOrdenacao.current + 1) % MODO_ORDENACOES.length;
    const modo = MODO_ORDENACOES[modoOrdenacao.current];
    setEstado((s) => {
      if (!s.nodes.length) return s;
      return {
        ...s,
        nodes: aplicarOrdenacao(s.nodes, s.edges, modo),
        // Reordenar também devolve os balões arrastados à posição calculada.
        baloes: {},
      };
    });
  }, []);

  /** Deslocamento de um balão de seta em relação à posição calculada no desenho. */
  const moverBalao = React.useCallback((chave: string, dx: number, dy: number) => {
    setEstado((s) => ({ ...s, baloes: { ...s.baloes, [chave]: { dx, dy } } }));
  }, []);

  /**
   * Setas do roteiro fiscal: troca a nota de lugar com a vizinha. `chaves` é a
   * ordem exibida agora — a do resultado, que já reflete a ordem fixada antes.
   * Devolve a ordem nova (ou `null` se a nota já está na ponta).
   */
  const moverNf = React.useCallback(
    (chaves: string[], chave: string, direcao: -1 | 1): string[] | null => {
      const i = chaves.indexOf(chave);
      const j = i + direcao;
      if (i < 0 || j < 0 || j >= chaves.length) return null;
      const ordem = [...chaves];
      [ordem[i], ordem[j]] = [ordem[j], ordem[i]];
      setEstado((s) => ({ ...s, ordemNfs: ordem }));
      return ordem;
    },
    [],
  );

  // Pedido de cálculo com atraso: arrastar não é motivo para recalcular.
  const [pedido, setPedido] = React.useState<PedidoCalculo | null>(null);
  React.useEffect(() => {
    const id = window.setTimeout(() => {
      setPedido({
        contexto,
        nodes: estado.nodes,
        edges: estado.edges,
        produto: estado.produto,
        anidroDiferido: estado.anidroDiferido,
        precoCompra: estado.precoCompra,
        precoVenda: estado.precoVenda,
        volume: estado.volume,
        nome: estado.nome,
        ordemNfs: estado.ordemNfs ?? [],
      });
    }, 250);
    return () => window.clearTimeout(id);
  }, [estado, contexto]);

  return {
    estado,
    /** O estado veio da sessão — quem usa o hook não deve sobrescrevê-lo com o modelo. */
    restaurado: salvo !== null,
    pedido,
    modoLigar,
    ligandoDe,
    alterar,
    substituirGrafo,
    adicionarNo,
    removerNo,
    atualizarNo,
    removerAresta,
    clicarNo,
    alternarModoLigar,
    ordenarFluxo,
    moverBalao,
    moverNf,
  };
}

export type OperacaoControle = ReturnType<typeof useOperacao>;
